import { Component, inject, OnInit, signal } from '@angular/core';
import { AbstractControl, FormBuilder, ReactiveFormsModule, ValidationErrors, ValidatorFn, Validators } from '@angular/forms';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { ButtonModule } from 'primeng/button';
import { PasswordModule } from 'primeng/password';

import { AuthService } from '../../auth.service';

@Component({
  selector: 'app-set-password-page',
  templateUrl: './set-password.page.html',
  styleUrl: './set-password.page.scss',
  imports: [
    ButtonModule,
    PasswordModule,
    ReactiveFormsModule,
    RouterLink,
  ],
})
export class SetPasswordPage implements OnInit {
  private readonly authService = inject(AuthService);
  private readonly formBuilder = inject(FormBuilder);
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);

  public readonly form = this.formBuilder.group({
    password: ['', [
      Validators.required,
      Validators.minLength(12),
      this.passwordStrengthValidator(),
    ]],
    confirmPassword: ['', [Validators.required]],
  }, { validators: this.passwordMatchValidator() });

  public readonly isLoading = signal(false);
  public readonly isValidating = signal(true);
  public readonly isTokenValid = signal(false);
  public readonly userEmail = signal<string | null>(null);
  public readonly errorMessage = signal<string | null>(null);

  private token = '';

  public ngOnInit(): void {
    this.token = this.route.snapshot.queryParamMap.get('token') || '';

    if (!this.token) {
      this.isValidating.set(false);
      this.isTokenValid.set(false);

      return;
    }

    this.authService.validateToken(this.token).subscribe({
      next: (response) => {
        this.isValidating.set(false);
        this.isTokenValid.set(response.valid);
        if (response.email) {
          this.userEmail.set(response.email);
        }
      },
      error: () => {
        this.isValidating.set(false);
        this.isTokenValid.set(false);
      },
    });
  }

  public onSubmit(): void {
    if (!this.form.valid || this.isLoading()) {
      return;
    }

    this.isLoading.set(true);
    this.errorMessage.set(null);

    const password = this.form.value.password as string;

    this.authService.setPassword(this.token, password).subscribe({
      next: () => {
        this.router.navigate(['/']);
      },
      error: () => {
        this.isLoading.set(false);
        this.errorMessage.set('Passwort konnte nicht festgelegt werden. Der Link ist möglicherweise abgelaufen.');
      },
    });
  }

  private passwordStrengthValidator(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      const value = control.value;
      if (!value) {
        return null;
      }

      const hasUpperCase = /[A-Z]/.test(value);
      const hasLowerCase = /[a-z]/.test(value);
      const hasNumber = /\d/.test(value);
      const hasSpecialChar = /[!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?]/.test(value);

      const errors: ValidationErrors = {};

      if (!hasUpperCase) {
        errors['missingUpperCase'] = true;
      }
      if (!hasLowerCase) {
        errors['missingLowerCase'] = true;
      }
      if (!hasNumber) {
        errors['missingNumber'] = true;
      }
      if (!hasSpecialChar) {
        errors['missingSpecialChar'] = true;
      }

      return Object.keys(errors).length > 0 ? errors : null;
    };
  }

  private passwordMatchValidator(): ValidatorFn {
    return (group: AbstractControl): ValidationErrors | null => {
      const password = group.get('password')?.value;
      const confirmPassword = group.get('confirmPassword')?.value;

      if (password && confirmPassword && password !== confirmPassword) {
        return { passwordMismatch: true };
      }

      return null;
    };
  }
}
